import type { SimCommand } from "../protocol/commands";
import type { CommandRejection, CommandResult, SimDriver } from "../protocol/driver";
import type { SimEvent } from "../protocol/events";
import type { GridSnapshot } from "../protocol/snapshot";
import { assertNever } from "../util/assertNever";
import { playCard } from "./cards";
import type { CardDef, StageDef } from "./definitions";
import { computeForecast } from "./forecast";
import { advancePhase, runSingleTick } from "./phases";
import { projectSnapshot } from "./project";
import { bindRecipe, cleanTile, dismantleHardware, installHardware, moveHardware, setPrecursor, type SetupResult } from "./setup";
import { createInitialState, type EngineState } from "./state";
import { setValve, type ValveResult } from "./valve";

export interface EngineDriverOptions {
  /**
   * Stage and cards are required, not defaulted. The engine ships no content
   * of its own beyond a test fixture, so there is nothing sensible to fall
   * back to — and a default is exactly how a fixture ends up in the shipped
   * game. Real content comes from @reagent/content.
   */
  stage: StageDef;
  cards: Record<string, CardDef>;
  seed?: number;
}

/**
 * The real simulation behind the SimDriver interface — the drop-in replacement
 * for MockDriver. The renderer never learns which one it is talking to.
 *
 * Fully deterministic: construct with the same stage and seed, issue the same
 * commands, and you get a byte-identical event stream (asserted in
 * engine.test.ts).
 */
export class EngineDriver implements SimDriver {
  private state: EngineState;
  private readonly cards: Record<string, CardDef>;
  private readonly subscribers = new Set<(event: SimEvent) => void>();
  private readonly seed: number;
  private readonly stage: StageDef;

  constructor(options: EngineDriverOptions) {
    this.stage = options.stage;
    this.cards = options.cards;
    this.seed = options.seed ?? 1;
    this.state = createInitialState(this.stage, this.seed);
  }

  getSnapshot(): GridSnapshot {
    return projectSnapshot(this.state, this.cards);
  }

  applyCommand(cmd: SimCommand): CommandResult {
    const events: SimEvent[] = [];
    let result: CommandResult;

    switch (cmd.type) {
      case "playCard": {
        // Resolved by instanceId FIRST: a card not in hand is a "notInHand"
        // rejection regardless of whether the catalogue even has the id,
        // which is what makes that rejection correct rather than incidental
        // (spec 0016).
        const instance = this.state.hand.find((ci) => ci.instanceId === cmd.instanceId);
        if (!instance) {
          result = { ok: false, rejection: notInHand(cmd) };
          break;
        }
        const card = this.cards[instance.cardId];
        if (!card) {
          result = { ok: false, rejection: unknownCard(cmd, instance.cardId) };
          break;
        }
        result = toCommandResult(
          cmd,
          playCard({ state: this.state, card, instanceId: cmd.instanceId, at: cmd.at, events, cards: this.cards, ...(cmd.recipeIds ? { recipeIds: cmd.recipeIds } : {}) }),
        );
        break;
      }

      case "connectPipe": {
        const instance = this.state.hand.find((ci) => ci.instanceId === cmd.instanceId);
        if (!instance) {
          result = { ok: false, rejection: notInHand(cmd) };
          break;
        }
        const card = this.cards[instance.cardId];
        if (!card) {
          result = { ok: false, rejection: unknownCard(cmd, instance.cardId) };
          break;
        }
        result = toCommandResult(
          cmd,
          playCard({
            state: this.state,
            card,
            instanceId: cmd.instanceId,
            at: null,
            fromNodeId: cmd.fromNodeId,
            toNodeId: cmd.toNodeId,
            events,
            cards: this.cards,
          }),
        );
        break;
      }

      case "advancePhase":
        result = toSetupCommandResult(cmd, advancePhase(this.state, this.cards, events));
        break;

      case "run":
        for (let i = 0; i < cmd.ticks; i++) {
          if (!runSingleTick(this.state, events)) break;
        }
        result = { ok: true };
        break;

      case "installHardware": {
        const card = this.cards[cmd.cardId];
        if (!card) {
          result = { ok: false, rejection: unknownCard(cmd, cmd.cardId) };
          break;
        }
        result = toSetupCommandResult(cmd, installHardware({ state: this.state, card, cmd, events, cards: this.cards }));
        break;
      }

      case "dismantleHardware":
        result = toSetupCommandResult(cmd, dismantleHardware({ state: this.state, cmd, events, cards: this.cards }));
        break;

      case "moveHardware":
        result = toSetupCommandResult(cmd, moveHardware({ state: this.state, cmd, events, cards: this.cards }));
        break;

      case "cleanTile":
        result = toSetupCommandResult(cmd, cleanTile({ state: this.state, cmd, events }));
        break;

      case "bindRecipe":
        result = toSetupCommandResult(cmd, bindRecipe({ state: this.state, cmd, events }));
        break;

      case "setPrecursor":
        result = toSetupCommandResult(cmd, setPrecursor({ state: this.state, cmd, events }));
        break;

      case "setValve":
        result = toValveCommandResult(cmd, setValve({ state: this.state, cmd, events }));
        break;

      default:
        return assertNever(cmd, "EngineDriver.applyCommand");
    }

    // Recomputed on entering Play and after each accepted command (spec
    // 0018) — never during projection. `computeForecast` itself is a no-op
    // outside Play, so calling it after every accepted command (including
    // ones that don't touch the board, like advancePhase into Draw) is
    // correct and cheap.
    if (result.ok) this.state.forecast = computeForecast(this.state);

    this.emitAll(events);
    return result;
  }

  /**
   * Advances one tick. Outside the Run phase this is a no-op returning [], so
   * a UI tick clock can call it freely without checking the phase first.
   */
  step(): SimEvent[] {
    const events: SimEvent[] = [];
    runSingleTick(this.state, events);
    this.emitAll(events);
    return events;
  }

  subscribe(fn: (event: SimEvent) => void): () => void {
    this.subscribers.add(fn);
    return () => this.subscribers.delete(fn);
  }

  /** Restarts the stage from the same seed. */
  reset(): void {
    this.state = createInitialState(this.stage, this.seed);
  }

  /**
   * Escape hatch onto raw engine state for tests and the replay CLI — it lets
   * a test stack the deck or force a hazard without a contrived setup. Not for
   * the renderer: everything the UI needs is on the SimDriver interface.
   */
  get internals(): EngineState {
    return this.state;
  }

  private emitAll(events: readonly SimEvent[]): void {
    for (const event of events) {
      for (const fn of this.subscribers) fn(event);
    }
  }
}

function notInHand(cmd: SimCommand): CommandRejection {
  return { command: cmd, code: "notInHand", message: "card is not in hand", sourceId: null };
}

function unknownCard(cmd: SimCommand, cardId: string): CommandRejection {
  return { command: cmd, code: "unknownCard", message: `unknown card: ${cardId}`, sourceId: null };
}

function toCommandResult(cmd: SimCommand, result: ReturnType<typeof playCard>): CommandResult {
  if (result.ok) return { ok: true };
  return {
    ok: false,
    rejection: { command: cmd, code: result.code, message: result.reason, sourceId: result.sourceId },
  };
}

function toSetupCommandResult(cmd: SimCommand, result: SetupResult): CommandResult {
  if (result.ok) return { ok: true };
  return {
    ok: false,
    rejection: { command: cmd, code: result.code, message: result.reason, sourceId: result.sourceId },
  };
}

function toValveCommandResult(cmd: SimCommand, result: ValveResult): CommandResult {
  if (result.ok) return { ok: true };
  return {
    ok: false,
    rejection: { command: cmd, code: result.code, message: result.reason, sourceId: result.sourceId },
  };
}
